import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/shared/lib/format';
import { formatBookingPrice } from '@/shared/lib/bookingPrice';
import type { BookingResponse } from '@/shared/api';
import { SectionCard } from './SectionCard';
import { calcNights, isOta, parseJsonArray } from './helpers';

interface ChannexTax {
  type?: string;
  name?: string;
  title?: string;
  amount?: number | string;
  total_price?: number | string;
}

function taxLabel(t: ChannexTax): string {
  return t.title ?? t.name ?? t.type ?? 'Steuer';
}

function taxValue(t: ChannexTax, currency: string): string {
  const raw = t.amount ?? t.total_price ?? null;
  if (raw == null) return '—';
  const num = typeof raw === 'number' ? raw : Number(raw);
  return Number.isNaN(num) ? '—' : formatCurrency(num, currency);
}

/** Aufschlüsselung des Buchungspreises; bei Kanalbuchungen so, wie der Kanal sie liefert. */
export function BillingCard({ booking }: { booking: BookingResponse }) {
  const { t } = useTranslation('manualBooking');
  const { currency } = booking;
  const nights = calcNights(booking.checkInDate, booking.checkOutDate);
  const taxes = parseJsonArray<ChannexTax>(booking.taxesJson);
  const collected = parseJsonArray<ChannexTax>(booking.collectedTaxesJson);
  const agreement = booking.priceAgreementJson
    ? (JSON.parse(booking.priceAgreementJson) as {
        Lines: { Key: string; Label: string; GrossAmount: number }[];
        Basis: string;
      })
    : null;
  const hasCleaning = booking.cleaningFee != null && booking.cleaningFee > 0;

  return (
    <SectionCard icon="Database" title={isOta(booking) ? 'Abrechnung laut Kanal' : 'Abrechnung'}>
      <div className="border-hairline rounded-control border">
        {agreement?.Lines.map((line) => (
          <Row
            key={line.Key}
            label={line.Label}
            value={formatCurrency(line.GrossAmount, currency)}
            strong
          />
        ))}
        {booking.priceBasis === 'TotalStay' && booking.baseRate != null && (
          <Row label={t('averageNight')} value={formatCurrency(booking.baseRate, currency)} muted />
        )}
        {!agreement && booking.baseRate != null && (
          <Row
            label={`${nights} ${nights === 1 ? 'Nacht' : 'Nächte'} × ${formatCurrency(booking.baseRate, currency)}`}
            value={formatCurrency(booking.baseRate * nights, currency)}
            strong
          />
        )}
        {!agreement && (
          <Row
            label="Reinigungsgebühr"
            value={hasCleaning ? formatCurrency(booking.cleaningFee!, currency) : 'inklusive'}
            muted={!hasCleaning}
          />
        )}
        {(booking.addons ?? []).map((addOn) => (
          <Row
            key={addOn.referenceId}
            label={addOn.label}
            value={formatCurrency(addOn.amount, addOn.currency)}
            strong
          />
        ))}
        {taxes.map((t, i) => (
          <Row key={`tax-${i}`} label={taxLabel(t)} value={taxValue(t, currency)} muted />
        ))}
        {collected.map((t, i) => (
          <Row
            key={`col-${i}`}
            label={`${taxLabel(t)} (einbehalten)`}
            value={taxValue(t, currency)}
            muted
          />
        ))}
        {booking.hasReservationLevelCharges && (
          <Row
            label="Weitere Beträge auf Reservierungsebene"
            value="nicht je Apartment aufteilbar"
            muted
          />
        )}
        <Row label="Gesamt" value={formatBookingPrice(booking)} strong />
      </div>
    </SectionCard>
  );
}

function Row({
  label,
  value,
  strong,
  muted,
}: {
  label: string;
  value: string;
  strong?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="border-hairline flex flex-col items-start justify-between gap-1 border-b px-4 py-2.5 text-sm last:border-b-0 sm:flex-row sm:items-center sm:gap-3">
      <span className={strong ? 'font-semibold' : 'text-ink-soft'}>{label}</span>
      <span className={muted ? 'text-ink-soft font-mono' : 'font-mono font-semibold tabular-nums'}>
        {value}
      </span>
    </div>
  );
}
